import { pdf } from '@react-pdf/renderer';
import { ModalContext } from 'app/app';
import axios from 'axios';
import { IconedButton } from 'components/IconedButton';
import { Pagination } from 'components/Pagination';
import {
  Animate,
  Grid,
  Search,
  Table,
  ViewContext,
} from 'components/lib';
import Certificate from 'components/modals/CertificatesModal/certificate';
import DateUtils from 'helpers/dateUtils';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.scss';

export function CertificatesC() {
  const { id: company_id } = useParams();
  const [payload, setPayload] = useState();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [page, setPage] = useState(1);
  const [searchQueryString, setSearchQueryString] = useState('');

  const format = new DateUtils();

  const { setModal } = useContext(ModalContext);
  const context = useContext(ViewContext);

  async function runAPI(page, search) {
    setLoading(true);

    const user = await axios.get('/api/user');
    setUser(user.data.data);

    await axios
      .get(
        `/api/certificate?page=${page}&search=${search}&company_id=${company_id}`
      )
      .then(async (res) => {
        setLoading(false);
        setPayload(res.data.data);

        var tempData = [];

        const timezone = await format.getTimezone();

        res.data.data.data.map((value) => {
          tempData.push({
            id: value.id,
            'Company Name': value.company_name,
            'Document Type': value.type,
            link: value.link,
            submission_number: value.submission_number,
            certificate_holder: value.certificate_holder,
            'Date Uploaded': format.timestamp(
              value.createdAt,
              timezone
            ),
            'Refreshed at':
              value.createdAt === value.updatedAt
                ? ''
                : format.timestamp(value.updatedAt, timezone),
            'Uploaded By': value.uploaded_by,
          });
        });

        setData(tempData);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);

        context.notification.show(
          'Failed to load table data',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  async function refreshCertificate(data) {
  setModal({
    name: 'success',
    data: {
      title: 'Updating this certificate',
      description:
        "Wait some minutes to our system finish certificate's update.",
    },
  });

  try {
    let signatureDataUrl = null;
    try {
      const signatureResponse = await fetch(user.signature);
      const blob = await signatureResponse.blob();
      signatureDataUrl = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching signature:", error);
      context.notification.show(
        'Error fetching signature image',
        'error',
        true,
        'toast',
        'x-circle'
      );
      setModal({});
      setLoading(false);
      return;
    }

    const info = await axios.get(`/api/quote/info/${data.submission_number}`);

    const pdfBlob = await pdf(
      <Certificate
        data={info.data.data}
        customerHolder={data.certificate_holder}
        signature={signatureDataUrl}
      />
    ).toBlob();

    const formData = new FormData();
    formData.append('file', pdfBlob, `certificate.pdf`);

    await axios
      .post('/api/certificate/file', formData)
      .then(async (res) => {
        await axios
          .patch('/api/certificate/' + data.id, {
            link: res.data.link,
          })
          .then(() => {
            setModal({});
            setLoading(false);

            runAPI(page, searchQueryString);

            context.notification.show(
              'Certificate refreshed',
              'success',
              true,
              'toast',
              'check-circle'
            );
          })
          .catch(() => {
            setLoading(false);
            setModal({});

            context.notification.show(
              'Error on create certificate',
              'error',
              true,
              'toast',
              'x-circle'
            );
          });
      })
      .catch(() => {
        setLoading(false);
        setModal({});

        context.notification.show(
          'Error on upload certificate to server',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  } catch (err) {
    setLoading(false);
    setModal({});

    context.notification.show(
      'Failed to load certificate details.',
      'error',
      true,
      'toast',
      'x-circle'
    );
  }
}

  useEffect(() => {
    runAPI(page, searchQueryString);
  }, [searchQueryString]);

  useEffect(() => {
    runAPI(page, '');
  }, []);

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <IconedButton
            size="180px"
            color="#d32425"
            textColor="white"
            icon="plus-circle"
            text="Create certificate"
            marginRight
            action={() =>
              setModal({
                name: 'certificates_modal',
                data: {
                  company_id,
                },
                action: () => runAPI(payload.page, searchQueryString),
              })
            }
          />
        </div>
      </div>
      <Grid cols="1">
        <div className={styles.wrapper}>
          <div style={{ margin: 20 }}>
            <div style={{ marginBottom: 30 }}>
              <Search
                callback={(query) => {
                  setSearchQueryString(query);
                }}
              />
            </div>
            <Table
              loading={loading}
              data={payload && payload.data ? data : null}
              hide={[
                'id',
                'link',
                'submission_number',
                'certificate_holder',
                'send_to',
              ]}
              badge={{ col: 'Document Type', color: 'gray' }}
              actions={{
                custom: [
                  {
                    icon: 'refresh-ccw',
                    tooltip:
                      'Refresh this certificate with updated data',
                    action: async (row) => {
                      await refreshCertificate(row);
                    },
                    color: '#637381',
                  },
                  {
                    icon: 'download-cloud',
                    tooltip: 'Download this certificate',
                    action: (row) => {
                      window.open(row.link);
                    },
                    color: '#637381',
                  },
                ],
              }}
            />
            {payload && (
              <Pagination
                pagesCount={
                  payload.pages_count === 0 ? 1 : payload.pages_count
                }
                active={payload.page}
                onChange={async (newPage) => {
                  setPage(newPage);
                  runAPI(newPage, searchQueryString);
                }}
              />
            )}
          </div>
        </div>
      </Grid>
    </Animate>
  );
}
